import type { ChatboxProps } from "../types";
import { callApiObservable } from "@/api";
import { getConversationRuntimeStatusApi } from "@/api/messagingApi";
import type { FormValue } from "@/model/form";
import {
  MessagingEventType,
  type MessagingMessageType,
  MessagingStatus,
  type MessagingMessage,
  type MessagingMessagePayloadMedia,
  type MessageFeedbackType,
} from "@/model/messaging";
import type { ConversationChatStore } from "@/modules/conversation/ConversationChatStore";
import { DeploymentIntegrationType } from "@/modules/deployment/api";
import { mergeSkipError } from "@/utils/rxjs";
import { BaseStore, createAtomStore } from "@deputy-ai/common/jotai";
import { useCreation } from "ahooks";
import { atom, type PrimitiveAtom } from "jotai";
import { useEffect, type PropsWithChildren } from "react";
import {
  debounceTime,
  EMPTY,
  finalize,
  lastValueFrom,
  map,
  mergeMap,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
} from "rxjs";

export class ChatboxStore extends BaseStore {
  options: PrimitiveAtom<ChatboxProps | null>;

  conversationChatStore = atom(
    (get) => get(this.options)?.conversationChatStore
  );

  debugModeEnabled = atom(
    (get) => get(this.options)?.debugMode?.enabled ?? false
  );
  isShowDebugMessage = atom(
    (get) => get(this.options)?.debugMode?.showMessage ?? false
  );

  unmarkedMessages = atom<Map<string, MessagingMessage>>(new Map());

  private readonly markAsRead$ = new Subject<void>();
  private statusSubscription: Subscription | undefined = new Subscription();

  convertedMsgs = atom((get) => {
    const messages = get(this.options)?.messages;

    if (messages) {
      return messages;
    }

    const conversationStore = this.getConversationChatStore();
    if (!conversationStore) {
      return [];
    }

    return get(conversationStore.messages);
  });
  visibleMessages = atom((get) => {
    const convertedMsgs = get(this.convertedMsgs);

    const enabledDebugMode = get(this.debugModeEnabled);
    const isShowDebugMessage = get(this.isShowDebugMessage);
    if (!enabledDebugMode || !isShowDebugMessage) {
      return convertedMsgs.filter(
        (item) => item.eventType !== MessagingEventType.Debug
      );
    }

    return convertedMsgs;
  });

  lastMessage = atom((get) => get(this.convertedMsgs).at(-1));

  noMoreMessages = atom((get) => {
    const conversationChatStore = get(this.conversationChatStore);
    if (!conversationChatStore) {
      return true;
    }
    return get(conversationChatStore.noMoreMessages);
  });

  showBackBottom = atom(false);

  readonly #isRunning = atom(false);
  inputDisabled = atom((get) => {
    const options = get(this.options);
    if (options?.input?.disabled) {
      return true;
    }

    return false;
  });

  canSendMessage = atom((get) => {
    const conversationChatStore = get(this.conversationChatStore);

    if (conversationChatStore) {
      const canSend = get(conversationChatStore.canSendMessage);
      if (!canSend) {
        return false;
      }
    }

    const inputDisabled = get(this.inputDisabled);
    if (inputDisabled) {
      return false;
    }

    const disabledInput = get(this.#isRunning);
    return !disabledInput;
  });

  newCount = atom(0);

  images = atom((get) => {
    return get(this.convertedMsgs)
      .filter((item) => !!item.payload.media)
      .map((item) => ({
        alt: item.payload.media?.altText,
        src: item.payload.media?.mediaUrl,
        message: item,
      }));
  });

  isBotRunning = atom((get) => {
    const integration = get(this.options)?.conversation?.metadata
      ?.current_integration as any;
    return integration?.type === DeploymentIntegrationType.ACTIVE;
  });

  getConversationChatStore() {
    return this.get(this.conversationChatStore);
  }

  getRequiredConversationChatStore() {
    const conversationChatStore = this.getConversationChatStore();
    if (!conversationChatStore) {
      throw new Error("conversationStore is required");
    }
    return conversationChatStore;
  }

  constructor(options?: ChatboxProps) {
    super();

    this.options = atom(options ?? null);
  }

  setOptions(options: ChatboxProps) {
    this.set(this.options, options);
  }

  init() {
    return mergeSkipError(
      this._handleSummarize(),
      this._handleMessageStatusChange()
    );
  }

  loadMore() {
    const conversationChatStore = this.getRequiredConversationChatStore();
    const isLoading = this.get(conversationChatStore.isLoadingMessages);
    if (isLoading) {
      return;
    }
    return conversationChatStore.loadMore();
  }

  async sendMessage(message: string, media?: MessagingMessagePayloadMedia) {
    this.checkRuntimeStatus();
    return await lastValueFrom(
      this.getRequiredConversationChatStore()
        .sendNewMessage({
          content: message,
        })
        .pipe(
          tap(({ message }) => {
            const onSendMessageSuccess = this.get(this.options)?.callbacks
              ?.onSendMessageSuccess;
            onSendMessageSuccess?.(message);
          })
        )
    );
  }

  async sendFile(args: { file: File; fileType: MessagingMessageType }) {
    return await lastValueFrom(
      this.getRequiredConversationChatStore().sendNewFile(args)
    );
  }

  updateMessage(message: MessagingMessage) {
    const conversationChatStore = this.getRequiredConversationChatStore();

    conversationChatStore.updateMessage(message.uid, (prev) => ({
      ...prev,
      ...message,
    }));
  }

  async sendMessageFeedback(args: {
    messageUid: string;
    feedbackType: MessageFeedbackType;
  }) {
    return await lastValueFrom(
      this.getRequiredConversationChatStore().sendMessageFeedback(args)
    );
  }

  async resendMessage(messageUid: string) {
    return await lastValueFrom(
      this.getRequiredConversationChatStore().resendMessage(messageUid)
    );
  }

  async submitForm(args: {
    messageUid: string;
    formValues: Record<string, FormValue>;
  }) {
    return await lastValueFrom(
      this.getRequiredConversationChatStore().submitForm(args)
    );
  }

  pushUnmarkedMessage(message: MessagingMessage) {
    this.set(this.unmarkedMessages, (prev) => {
      prev.set(message.uid, message);
      return prev;
    });
  }

  setIsRunning(running: boolean) {
    this.set(this.#isRunning, running);
  }

  setShowBackBottom(show: boolean) {
    this.set(this.showBackBottom, show);
  }

  setNewCount(count: number) {
    this.set(this.newCount, count);
  }

  checkRuntimeStatus() {
    this.setIsRunning(true);
    const observable$ = timer(10_000, 5_000).pipe(
      mergeMap(() => this.#getConversationRuntimeStatus()),
      takeWhile((isRunning) => {
        const curIsRunning = this.get(this.#isRunning);
        const shouldContinue = !!isRunning || curIsRunning;
        this.setIsRunning(!!isRunning);
        return shouldContinue;
      }, true),
      take(8)
    );

    this.statusSubscription?.unsubscribe();
    this.statusSubscription = observable$.subscribe();
  }

  stopStatusTracking() {
    this.setIsRunning(false);
    this.statusSubscription?.unsubscribe();
    this.statusSubscription = undefined;
  }

  #getConversationRuntimeStatus() {
    const conversation = this.get(this.options)?.conversation;
    if (!conversation?.uid) {
      return of();
    }
    return callApiObservable(async () => {
      return await getConversationRuntimeStatusApi({
        path: {
          installedAppUid: conversation.installedAppUid,
          orgUid: conversation.orgUid,
          conversationUid: conversation.uid,
        },
      });
    }).pipe(map((res) => res.isRunning));
  }

  private _handleSummarize() {
    const summarizeOption = atom((get) => get(this.options)?.summarize ?? {});
    const summarizeEnabled = atom(
      (get) => get(summarizeOption)?.enabled ?? false
    );
    const summarizing = atom(false);

    return this.fromAtom(summarizeEnabled).pipe(
      switchMap((summarizeEnabled) => {
        if (!summarizeEnabled) {
          return EMPTY;
        }

        return this.fromAtom(this.visibleMessages, {
          skipFirst: true,
        }).pipe(
          map((visibleMessages) => {
            const {
              messageCount = 3,
              checkIfSummarize,
              summarize,
            } = this.get(summarizeOption);

            const isSummarizing = this.get(summarizing);
            if (isSummarizing) {
              return;
            }

            if (visibleMessages.length < messageCount) {
              return;
            }

            if (
              checkIfSummarize &&
              !checkIfSummarize?.({ messages: visibleMessages })
            ) {
              return;
            }

            if (!summarize) {
              console.error("summarize is not provided");
              return;
            }

            return {
              summarize,
              visibleMessages,
            };
          }),
          mergeMap((data) => {
            if (!data) {
              return EMPTY;
            }

            const { summarize, visibleMessages } = data;

            this.set(summarizing, true);
            return summarize({ messages: visibleMessages }).pipe(
              finalize(() => {
                this.set(summarizing, false);
              })
            );
          })
        );
      })
    );
  }

  private _handleMessageStatusChange() {
    return this.markAsRead$.pipe(
      debounceTime(1000),
      switchMap(() => {
        const messageUids = this.get(this.unmarkedMessages).keys().toArray();
        const userId = this.get(this.options)?.participants?.myself?.externalId;
        if (!userId) {
          return of();
        }
        return this.#batchSetMessageStatus(
          userId,
          MessagingStatus.Read,
          messageUids
        ).pipe(
          tap(() => {
            this.set(this.unmarkedMessages, (prev) => {
              messageUids.forEach((item) => prev.delete(item));
              return prev;
            });
          })
        );
      })
    );
  }

  debouncedSetMessageStatus() {
    this.markAsRead$.next();
  }

  #batchSetMessageStatus(
    userId: string,
    status: MessagingStatus,
    messageUids: string[]
  ) {
    const conversation = this.get(this.options)?.conversation;
    if (!conversation?.uid) {
      return of();
    }
    return this.getRequiredConversationChatStore().updateMessagesStatus({
      deliveredStatus: { [userId]: status },
      messageUids,
    });
  }
}

export const { ChatboxProvider, useChatboxStore } = createAtomStore({
  name: "Chatbox",
  StoreClass: ChatboxStore,
});

export const ChatboxStoreProvider = ({
  options,
  children,
  conversationStore,
}: PropsWithChildren<{
  conversationStore: ConversationChatStore;
  options: ChatboxProps;
}>) => {
  const store = useCreation(
    () => new ChatboxStore(options),
    [conversationStore]
  );

  useEffect(() => {
    store.set(store.options, options);
  }, [options]);

  return <ChatboxProvider store={store}>{children}</ChatboxProvider>;
};
